import React from 'react';

/**
 * QuotaDemoBox — renders:
 *   1. "Queries Used: N of QUOTA" counter
 *   2. "View Knowledge Graph Demo" link
 *   3. Optional Control Centre callout/hint beneath it.
 *
 * Used in three places:
 *   • Empty home state (before the first query)
 *   • Under the first query's Q: bubble
 *   • Under the first query's suggestions panel
 *
 * Props:
 *   used, quota            — integers for counter
 *   demoViewed, onOpenDemo — demo button state + handler
 *   showCCHint             — whether to render the CC callout below the box
 *   onOpenCC, onDismissCC  — CC callout handlers
 *   isMobile               — responsive sizing
 *   variant                — 'home' | 'inline' (slight style tweak)
 *   testIdSuffix           — string suffix so duplicate instances have unique test ids
 */
export const QuotaDemoBox = ({
  used,
  quota,
  demoViewed,
  onOpenDemo,
  showCCHint,
  onOpenCC,
  onDismissCC,
  isMobile,
  variant = 'inline',
  testIdSuffix = '',
}) => {
  const remaining = quota - used;
  const counterColor = remaining === 0 ? '#fcd34d' : '#67e8f9';
  const suffix = testIdSuffix ? `-${testIdSuffix}` : '';

  // Home variant keeps the historical 142px indent so it visually aligns
  // with the large welcome hero. Inline variant centers inside the thread.
  const wrapperClass =
    variant === 'home'
      ? `mt-${isMobile ? '4' : '5'} ${isMobile ? 'ml-9' : ''}`
      : 'mt-4 flex justify-center';
  const wrapperStyle =
    variant === 'home' && !isMobile ? { marginLeft: '142px' } : {};

  return (
    <div
      className={wrapperClass}
      style={wrapperStyle}
      data-testid={`quota-demo-bar${suffix}`}
    >
      <div className="flex flex-col items-center gap-3">
        <div
          className={`inline-block text-center rounded-xl ${isMobile ? 'px-4 py-3' : 'px-6 py-4'}`}
          style={{
            border: '1px solid rgba(34,211,238,0.55)',
            backgroundColor: 'rgba(8,30,42,0.55)',
            boxShadow: '0 0 18px rgba(34,211,238,0.12)',
            minWidth: isMobile ? '220px' : '320px',
          }}
        >
          <p
            className={`${isMobile ? 'text-base' : 'text-xl'} font-semibold tracking-wide`}
            style={{ color: counterColor }}
            data-testid={`quota-text${suffix}`}
          >
            Queries Used: <span style={{ fontWeight: 800 }}>{used}</span> of{' '}
            <span style={{ fontWeight: 800 }}>{quota}</span>
          </p>
          <button
            onClick={onOpenDemo}
            data-testid={`open-demo-link${suffix}`}
            className={`mt-2 ${isMobile ? 'text-base' : 'text-xl'} font-semibold text-cyan-300 hover:text-cyan-200 underline underline-offset-4 decoration-dotted decoration-cyan-400/60 transition-colors block w-full`}
          >
            {demoViewed ? 'View Knowledge Graph Demo again' : 'View Knowledge Graph Demo'}
          </button>
        </div>

        {/* Control Centre callout — same copy as the icon tooltip, but
            embedded inline so the user always sees it even when the
            corner icons scroll out of view. Hides permanently once the
            user opens the CC or dismisses the hint. */}
        {showCCHint && (
          <div
            className="rounded-xl shadow-2xl"
            style={{
              width: isMobile ? '100%' : '360px',
              maxWidth: '92vw',
              background:
                'linear-gradient(135deg, rgba(8,30,42,0.97) 0%, rgba(15,40,60,0.97) 100%)',
              border: '1px solid rgba(34,211,238,0.55)',
              boxShadow:
                '0 0 0 1px rgba(34,211,238,0.35), 0 12px 32px rgba(0,0,0,0.6), 0 0 28px rgba(34,211,238,0.25)',
            }}
            data-testid={`cc-inline-callout${suffix}`}
          >
            <div className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-cyan-400 text-black text-xs font-bold">
                  !
                </span>
                <span className="text-xs uppercase tracking-[0.18em] text-cyan-300 font-semibold">
                  Heads up
                </span>
              </div>
              <p className="text-sm text-gray-100 leading-relaxed">
                Please check out your{' '}
                <span className="font-bold text-cyan-300">Control Centre</span>{' '}
                and your{' '}
                <span className="font-bold text-cyan-300">Accuracy Controls</span>.
                We wanted to give you full control over your data and make it
                as simple as possible at the same time.
              </p>
              <div className="mt-3 pt-3 border-t border-cyan-400/20 flex items-center justify-between gap-3">
                <button
                  onClick={onOpenCC}
                  className="text-xs font-semibold bg-cyan-500 hover:bg-cyan-400 text-black px-3 py-1.5 rounded-md transition-colors"
                  data-testid={`cc-inline-open${suffix}`}
                >
                  Open Control Centre
                </button>
                <button
                  onClick={onDismissCC}
                  className="text-[10px] uppercase tracking-wider text-gray-400 hover:text-gray-200 transition-colors"
                  data-testid={`cc-inline-dismiss${suffix}`}
                >
                  Don't Show
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuotaDemoBox;
